import type { CSSProperties } from "react";

// ArcLine design tokens.
//
// Low-vision rule (still in force): every text tone must clear AAA (7:1) on all
// three grounds. Re-verify with a contrast check after touching any ground or
// text token — the bento rollout lifted the grounds, which eats headroom.
//
// The app's visual language is the bento system: tonal surfaces, hairline
// borders, generous radii, rings and dot-tracks instead of text percentages.
// See the bento* builders below and components/bento/.
export const c = {
  // VETS Federal theme — built from the VETS house colors used in the Word
  // reports (navy 1B3A5C, blue 1E50A2, gold 8A6A1F, red CE1126): light gray
  // pages, white cards with a visible edge, a navy sidebar, and dark text.
  //
  // Readability rule: every token used as TEXT clears WCAG AAA (7:1) on the
  // white card, the row tint (panel2) and the page (bg); on the gray button
  // chips (soft.card / soft.cardHi) colored labels stay above 6.3:1. Fills that
  // carry text (blue, red, accent, violet, green, amber) take `onAccent`
  // (white), and each of those fills clears 7:1 against white.
  bg: "#EEF2F6",
  panel: "#FFFFFF",
  panel2: "#F3F6F9",
  line: "#D3DBE4",
  line2: "#AFBBC8",
  text: "#1F2933",         // 14.7:1 on white
  textHi: "#0E1E30",       // 17.1:1
  muted: "#3F4C5A",        // 9.1:1 on white, 8.3:1 on panel2
  muted2: "#2A3644",       // 12.3:1
  muted3: "#3F4C5A",
  onAccent: "#FFFFFF",     // text sitting on any filled accent
  red: "#B3101F",          // fill (7.5:1 with white text)
  redHi: "#9B0F1C",        // text, 8.6:1
  blue: "#1E50A2",         // VETS blue fill (7.8:1 with white text)
  blueHi: "#1A4690",       // text, 9.0:1
  cyan: "#0A5870",         // 7.9:1
  green: "#135C37",        // 8.1:1
  amber: "#6E4700",        // 8.0:1 (dark gold, readable as text)
  gold: "#8A6A1F",         // VETS gold — rules and highlights, not body text
  violet: "#4F3494",       // 9.5:1
  teal: "#0C5A54",
  navItem: "#E3E9F0",
  // Sidebar: VETS navy with white type. The R3x logo is white, so it keeps
  // reading exactly as before on this ground.
  side: {
    bg: "#10243A",
    text: "#E8EEF5",
    muted: "#B9C6D5",
    hint: "#9FB0C3",
    active: "#1E50A2",
    hover: "#1A3452",
    line: "rgba(255,255,255,0.14)",
  },
  soft: {
    // Names kept from the old bento palette so every view picks the new look
    // up without being rewritten: `tile` is a card, `card` is a button/row
    // chip, `cardHi` is a selected row or a row inside a card.
    card: "#E6ECF2",
    cardHi: "#DDE6F0",
    accent: "#1E50A2",
    accentDim: "rgba(30,80,162,0.10)",
    ground: "#EEF2F6",
    tile: "#FFFFFF",
    tileHi: "#F3F6F9",
    accent2: "#1E50A2",
    green: "#135C37",
    amber: "#6E4700",
    coral: "#9B0F1C",
    violet: "#4F3494",
    cyan: "#0A5870",
    track: "#D3DBE4",
    trackDim: "#E6EBF1",
  },
};

// DejaVu Sans / DejaVu Sans Mono (the Modern Minimalist pairing), bundled in
// public/fonts so the app never waits on a font CDN. Verdana is the closest
// system fallback (same Vera lineage, same wide, legible letterforms).
export const font = {
  sans: "'DejaVu Sans', Verdana, 'Segoe UI', system-ui, sans-serif",
  mono: "'DejaVu Sans Mono', Menlo, Consolas, monospace",
  display: "'DejaVu Sans', Verdana, 'Segoe UI', system-ui, sans-serif",
};

// Softened, borderless card building blocks — the visual language piloted on
// the Command Dashboard/Executive Summary and now the default for the rest of
// the app: tonal cards instead of bordered panels, sans-serif labels instead
// of all-caps mono, pill buttons instead of bordered rectangles. Import these
// instead of hand-rolling the c.panel/c.line/font.mono combination.
// These three are imported by ~27 views. They were the pilot treatment; they
// now carry the bento look so every view that already uses them converts
// without touching 27 files. Geometry moves conservatively (radius 16→22,
// padding 18/20→20/22) so dense pages reflow slightly, not dramatically —
// bentoCard below is the fuller 24px/22px treatment for converted layouts.
export const softPanel: CSSProperties = {
  background: c.soft.tile,
  border: `1px solid ${c.line}`,
  borderRadius: 8,
  padding: "20px 22px",
  boxShadow: "0 1px 2px rgba(16,36,58,0.06)",
};
export const softPanelTitle: CSSProperties = { font: `700 15px ${font.sans}`, color: c.textHi, marginBottom: 14 };
export const softPill: CSSProperties = { font: `600 13.5px ${font.sans}`, color: c.muted2, background: c.soft.card, border: `1px solid ${c.line}`, borderRadius: 6, padding: "9px 14px", cursor: "pointer" };

// ── Bento building blocks ────────────────────────────────────────────────────
// The approved mockup's language, one step softer than softPanel above: 24px
// radius, no border at all, a tonal fill lifted off the page ground, and a
// shadow that reads as depth rather than as an outline. softPanel* stay
// untouched for the views that haven't been converted yet.
export const RADIUS = { card: 10, tile: 8, pill: 6 } as const;
export const SPACE = { pad: 22, gap: 20, tight: 14 } as const;

export const bentoCard: CSSProperties = {
  background: c.soft.tile,
  border: `1px solid ${c.line}`,
  borderRadius: RADIUS.card,
  padding: SPACE.pad,
  boxShadow: "0 1px 2px rgba(16,36,58,0.06)",
};
// The inner surface — nested cards, rows and chips sitting inside a bentoCard.
export const bentoTile: CSSProperties = {
  background: c.soft.tileHi,
  borderRadius: RADIUS.tile,
  padding: "16px 18px",
};
export const bentoTitle: CSSProperties = { font: `700 15.5px/24px ${font.sans}`, color: c.textHi, margin: 0 };
export const bentoNote: CSSProperties = { font: `500 13px/24px ${font.sans}`, color: c.muted, whiteSpace: "nowrap" };
export const pillBtn: CSSProperties = {
  font: `600 13.5px/1 ${font.sans}`,
  color: c.muted2,
  background: c.soft.card,
  border: `1px solid ${c.line}`,
  borderRadius: RADIUS.pill,
  padding: "9px 14px",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
};

// Digits that sit in a column must not jitter as values change — apply this
// anywhere numbers stack (stat tiles, legends, funnel rows, summary lists).
export const tnum: CSSProperties = { fontVariantNumeric: "tabular-nums" };

// A colored circular icon badge with a soft outward glow. `tone` is any of the
// c.soft semantic colors; the glow is derived from it so callers pass one value.
export function iconBadge(tone: string, size = 36): CSSProperties {
  return {
    width: size,
    height: size,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    fontSize: Math.round(size * 0.42),
    lineHeight: 1,
    color: tone,
    background: withAlpha(tone, 0.12),
    flex: "none",
  };
}

// Translucent variant of a token. Accepts #rgb/#rrggbb and passes anything else
// (rgba(), a CSS keyword) straight through — callers shouldn't have to care.
export function withAlpha(color: string, alpha: number): string {
  const hex = color.trim();
  if (!hex.startsWith("#")) return hex;
  const h = hex.length === 4
    ? hex.slice(1).split("").map((ch) => ch + ch).join("")
    : hex.slice(1);
  if (h.length !== 6) return hex;
  const n = parseInt(h, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

// Bento-era score bands. Mirrors scoreColor()'s thresholds so a score reads the
// same everywhere, but resolves to the softer c.soft tones.
export function softScoreColor(n: number | null): string {
  if (n == null) return c.muted;
  return n >= 88 ? c.soft.green : n >= 78 ? c.soft.accent2 : n >= 70 ? c.soft.amber : c.soft.coral;
}

export function scoreColor(n: number | null): string {
  if (n == null) return c.muted;
  return n >= 88 ? c.green : n >= 78 ? c.blueHi : n >= 70 ? c.amber : c.muted;
}

export function fmt(v: number): string {
  if (!v) return "$0";
  return v >= 1000 ? `$${(v / 1000).toFixed(1)}B` : `$${v.toFixed(1)}M`;
}

// Shared across every Listing sub-tab's status vocabulary (register docs, CP
// gates/checklist, listing requirements, filings) so "met"/"ready"/"pending"
// etc. always render the same color regardless of which page it's on.
const PROCESS_STATUS_COLOR: Record<string, string> = {
  pending: c.muted, unknown: c.muted, not_started: c.muted,
  in_progress: c.amber, drafting: c.amber, received: c.blueHi, produced: c.blueHi,
  ready: c.cyan, submitted: c.cyan, met: c.green, accepted: c.green, effective: c.green, filed: c.green,
  not_met: c.redHi, waived: c.muted2,
  open: c.redHi, done: c.green,
};
export function processStatusColor(s: string): string {
  return PROCESS_STATUS_COLOR[s] ?? c.blueHi;
}
